import { motion } from "framer-motion";
import { AnimatedTimeline } from "@/components/ui/AnimatedTimeline";

interface StepsTimelineProps {
  t: (key: string) => string;
  dir: "ltr" | "rtl";
}

export function StepsTimeline({ t, dir }: StepsTimelineProps) {
  const isRtl = dir === "rtl";
  const steps = [
    { title: t("home.step1Title"), text: t("home.step1Text") },
    { title: t("home.step2Title"), text: t("home.step2Text") },
    { title: t("home.step3Title"), text: t("home.step3Text") },
    { title: t("home.step4Title"), text: t("home.step4Text") },
    { title: t("home.step5Title"), text: t("home.step5Text") },
  ];

  return (
    <section id="section-journey" className="py-24" data-testid="section-steps">
      <div className="max-w-4xl mx-auto px-6">
        <motion.h2
          className="text-2xl md:text-3xl font-serif font-bold text-center text-foreground mb-4 max-w-2xl mx-auto"
          style={{ fontFamily: "'Playfair Display', serif" }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="text-steps-title"
        >
          {t("home.stepsTitle")}
        </motion.h2>

        <motion.div
          className="w-12 h-0.5 bg-primary/40 mx-auto mb-14"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />

        <AnimatedTimeline steps={5} isRtl={isRtl}>
          <div className="space-y-8">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                className={`relative flex items-start gap-6 ${isRtl ? "flex-row-reverse" : ""}`}
                initial={{ opacity: 0, x: isRtl ? 20 : -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`step-${i}`}
              >
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  className="relative z-10 w-12 h-12 rounded-full bg-primary text-primary-foreground font-serif text-lg font-bold flex items-center justify-center shadow-lg shadow-primary/20 shrink-0"
                >
                  {i + 1}
                </motion.div>
                <div className="pt-2.5 pb-2 flex-1">
                  <h3
                    className="text-base font-semibold text-foreground mb-1"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatedTimeline>
      </div>
    </section>
  );
}
